import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { env } from '../env';
import { setSessionToken } from '../accounts';

/**
 * Magic-link / pre-auth-claim redemption route.
 *
 * Reached when a Universal Link (iOS) / App Link (Android) like
 *   https://mobile.underlings.com/claim/<code>
 * is tapped — the native OS routes the user into the app, the
 * <DeepLinkBridge> in App.tsx forwards the URL path to React Router,
 * and we land here.
 *
 * Same code path is used by the mobile PWA when the user doesn't have
 * the native app installed — the browser opens the same URL and the
 * PWA handles it. The web magic-link landing in apps/web/MagicLinkPage
 * remains for legacy `/r/<code>` URLs.
 */
export function Claim() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) {
      setError('This link is malformed.');
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${env.VITE_API_URL}/auth/magic-link/redeem`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ code }),
        });
        const data = (await res.json()) as {
          ok: boolean;
          token?: string;
          destination?: string;
          error?: string;
        };
        if (cancelled) return;
        if (!res.ok || !data.ok || !data.token) {
          setError(data.error ?? 'This link is no longer valid.');
          return;
        }
        setSessionToken(data.token);
        // Use replace so the back button doesn't return to the
        // now-invalidated claim URL.
        navigate(data.destination ?? '/', { replace: true });
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, navigate]);

  if (error) {
    return (
      <section className="min-h-dvh bg-background flex items-center justify-center px-6">
        <div className="rounded-2xl border border-border bg-card p-6 text-center shadow-sm max-w-sm">
          <p className="text-base font-semibold">This link is no longer valid.</p>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <p className="mt-4 text-xs text-muted-foreground">
            Reply to the WhatsApp or email we sent you and we&apos;ll send a fresh one.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-dvh bg-background flex items-center justify-center">
      <Loader2 className="size-6 animate-spin text-muted-foreground" />
    </section>
  );
}
